import React, { useEffect, useRef } from 'react'
import { useGroupsContext } from '@fs/zion-tree-groups'
import { useZionAxios } from '@fs/zion-axios'
import AllTreeGroupsProvider from './AllTreeGroupsProvider'

const filesProcessingStatuses = ['UPLOADING', 'PROCESSING', 'PROCESSED']

export default function AllTreesData({ children }) {
  const { groups, getAndSetGroups } = useGroupsContext()
  const [{ data: uploadedFiles = [], loading }, refetchFiles] = useZionAxios('/service/tree/gedcom/upload/files')
  const pollingIntervalRef = useRef(null)
  const hasLoadedInitiallyRef = useRef(false)

  // Only include files with FAILED status and no treeId
  const failedFiles = uploadedFiles.filter((file) => !file.treeId && file.fileStatus?.includes('FAILED'))

  const processFiles = uploadedFiles.filter((file) => !file.treeId && filesProcessingStatuses.includes(file.fileStatus))

  const allGroups = [...(groups || []), ...(processFiles || [])].map((group) => {
    const fileForCurrentGroup = uploadedFiles.find((file) => file.treeId === group.treeIds?.[0])
    // When a tree has Errors in the tree and has been assigned a treeId, we don't want to show failed but instead imported.
    const fileStatus = fileForCurrentGroup?.fileStatus.includes('FAILED') ? 'IMPORTED' : fileForCurrentGroup?.fileStatus
    if (fileForCurrentGroup) {
      return { ...group, ...fileForCurrentGroup, fileStatus }
    }
    return group
  })

  // Track when initial data has loaded
  useEffect(() => {
    if (uploadedFiles.length > 0 || !loading) {
      hasLoadedInitiallyRef.current = true
    }
  }, [uploadedFiles.length, loading])

  // Check if any file has IMPORTING status
  const hasImportingFiles = allGroups.some(
    (group) => group.fileStatus === 'IMPORTING' || filesProcessingStatuses.includes(group.fileStatus)
  )

  // Set up polling for importing files
  useEffect(() => {
    if (hasImportingFiles) {
      // Start polling every 10 seconds
      pollingIntervalRef.current = setInterval(() => {
        refetchFiles()
        getAndSetGroups(true)
      }, 10000)
    } else if (pollingIntervalRef.current) {
      // Clear polling if no importing files
      clearInterval(pollingIntervalRef.current)
      pollingIntervalRef.current = null
    }

    // Cleanup on unmount
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current)
      }
    }
  }, [getAndSetGroups, hasImportingFiles, refetchFiles])

  return (
    <AllTreeGroupsProvider
      allGroupsData={{
        allGroups,
        failedFiles,
        refetchFiles,
        uploadedFiles,
        loadingUploadedFiles: loading && !hasLoadedInitiallyRef.current,
      }}
    >
      {children}
    </AllTreeGroupsProvider>
  )
}
